<script lang="ts">
export default {
  name: 'DocumentViewerModal',
}
</script>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { Download, X, PanelLeftOpen, PanelLeftClose, FileX } from 'lucide-vue-next';

interface Props {
  show?: boolean;
  fileName: string;
  fetchDocument: (convertToPdf: boolean) => Promise<string>;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  document: null,
  fileName: '',
  fetchDocument: () => Promise.resolve(''),
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  'close': [];
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const documentFrame = ref<HTMLIFrameElement | null>(null);
const documentUrl = ref<string | null>(null);
const loading = ref(false);
const documentViewerMode = ref<'split' | 'full'>('full');

const isDocx = computed(() => props.fileName.toLowerCase().endsWith('.docx'));

const open = async () => {
  loading.value = true;
  dialogRef.value?.showModal();
  try {
    documentUrl.value = await props.fetchDocument(true);
    loading.value = false;
  } catch (error) {
    console.error('Error loading document:', error);
    loading.value = false;
  }
};

const close = () => {
  dialogRef.value?.close();
  emit('update:show', false);
  emit('close');

  if (documentUrl.value) {
    URL.revokeObjectURL(documentUrl.value);
    documentUrl.value = null;
  }
};

const downloadDocument = async () => {
  try {
    let url = await props.fetchDocument(false);
    const anchorElement = document.createElement('a');
    anchorElement.href = url;
    anchorElement.download = props.fileName;
    anchorElement.setAttribute('target', '_blank');
    anchorElement.click();
    anchorElement.remove();
  } catch (err) {
    console.error(err);
  }
}

const toggleViewerMode = () => {
  if (documentViewerMode.value !== 'full') {
    documentViewerMode.value = 'full';
  } else {
    documentViewerMode.value = 'split';
  }
}

const onDocumentLoad = () => {
  // Document loaded successfully
};

const onDocumentError = () => {
  console.error('Error loading document in iframe');
  loading.value = false;
};

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.show) {
    close();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
});

watch(() => props.show, (newShow, oldShow) => {
  if (newShow && !oldShow) {
    open();
  }
});

defineExpose({
  open,
  close
});
</script>

<template>
  <Teleport to="body">
    <dialog
      class="tw:modal tw:z-[1001] tw:items-center"
      :class="{ 'tw:modal-open': show, }"
      :style="{ 'width': documentViewerMode === 'split' ? '50vw !important' : '90vw', 'background-color': documentViewerMode !== 'full' ? 'unset !important' : 'unset'}"
      ref="dialogRef"
    >
      <div
        class="tw:modal-box tw:w-[90%] tw:max-w-none tw:h-[90vh] tw:max-h-[90vh] tw:p-0 tw:rounded-xl tw:flex tw:flex-col">

        <div class="tw:flex tw:justify-between tw:items-center tw:p-4 tw:border-b tw:border-gray-200">
          <h2 class="tw:text-lg tw:font-semibold tw:truncate tw:mr-4">
            {{ props.fileName || 'Document' }}
          </h2>
          <div class="tw:flex tw:items-center tw:gap-2">

            <div class="tw:tooltip tw:tooltip-left" data-tip="Download Microsoft Word format">
              <div
                v-if="isDocx"
                role="button"
                tabindex="0"
                class="tw:btn tw:btn-sm tw:self-center"
                @click="downloadDocument"
              >
                <Download :size="16" /> Download DOCX
              </div>
            </div>

            <div v-if="documentViewerMode === 'full'" class="tw:tooltip tw:tooltip-bottom" data-tip="Move to left half of screen">
              <div
                role="button"
                tabindex="0"
                class="tw:btn tw:btn-sm tw:self-center tw:border-none tw:!bg-transparent"
                @click="toggleViewerMode"
              >
                <PanelLeftClose :size="16" />
              </div>
            </div>
            <div v-else-if="documentViewerMode === 'split'" class="tw:tooltip tw:tooltip-bottom" data-tip="Restore full screen">
              <div
                role="button"
                tabindex="0"
                class="tw:btn tw:btn-sm tw:self-center tw:border-none tw:!bg-transparent"
                @click="toggleViewerMode"
              >
                <PanelLeftOpen :size="16" />
              </div>
            </div>

            <div class="tw:tooltip tw:tooltip-bottom" data-tip="Close">
              <div
                role="button"
                tabindex="0"
                class="tw:ml-2 tw:btn tw:btn-xs tw:h-7 tw:w-9 tw:self-center tw:border-none tw:!bg-transparent"
                @click="close"
              >
                <X :size="16" />
              </div>
            </div>
          </div>
        </div>

        <!-- Document viewer content -->
        <div class="tw:flex-1 tw:overflow-hidden tw:bg-gray-50">
          <div v-if="loading" class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-2/3 tw:pb-20">
            <span class="tw:loading tw:loading-dots tw:loading-lg tw:mt-24 tw:text-gray-500"></span>
            <span class="tw:ml-3">Loading document...</span>
          </div>

          <div v-else-if="documentUrl" class="tw:h-full tw:w-full">
            <!-- PDF or document viewer using object/embed -->
            <object
              :data="documentUrl"
              type="application/pdf"
              class="tw:w-full tw:h-full tw:border-0"
              ref="documentFrame"
              @load="onDocumentLoad"
              @error="onDocumentError">
              <embed
                :src="documentUrl"
                type="application/pdf"
                class="tw:w-full tw:h-full tw:border-0" />
              <p class="tw:flex tw:items-center tw:justify-center tw:h-full tw:text-gray-500">
                <span>Your browser doesn't support PDF viewing.
                  <a :href="documentUrl" target="_blank" class="tw:text-blue-500 tw:underline">
                    Click here to download the document
                  </a>
                </span>
              </p>
            </object>
          </div>

          <div v-else class="tw:flex tw:items-center tw:justify-center tw:h-full tw:text-gray-500">
            <div class="tw:text-center">
              <FileX :size="48" class="tw:mb-2 tw:mx-auto" />
              <p>Unable to display document</p>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>